// frontend/src/components/ModuleDrawer.tsx
import React, { useEffect, useState } from 'react';
import { Box, Drawer, Typography, Chip, CircularProgress } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { brand, radii } from '../theme';
import { moduleIcons, uiIcons } from '../assets/icons/nav';
import NavIcon from '../shared/components/NavIcon';
import { useModuleDrawerStore } from '../store/moduleDrawerStore';
import { useTranslation } from '../store/languageStore';
import authService from '../shared/services/authService';

export const DRAWER_WIDTH = 320;

// Active sub-module radio colour. Selected items belong to an authenticated
// module, so the radio reads as "active/ready" in green (distinct from the
// orange selection border on the row itself).
const RADIO_ACTIVE_GREEN = '#2E9E3F';

// Generic sub-module shape. `id` is the value persisted/dispatched when the
// row is clicked (e.g. ProcessType string for Atlas). Children are optional
// second-level options shown indented under the row when it's the active
// selection.
export interface SubModuleItem {
  id: string;
  label: string;
  iconKey: keyof typeof moduleIcons;
  children?: SubModuleItem[];
  // When true the row is rendered greyed-out and cannot be selected.
  // Used today for Atlas variants that aren't ready yet (Einfuhr, EAS).
  disabled?: boolean;
}

// Declarative description of a "parent module" — a top-level module that
// renders a nested list of child rows underneath its header. Today this is
// driven by a name regex because the backend Module model lacks a
// parent_module_id field; once that lands, swap `matches` for an id lookup.
// Adding support for a new parent is a single entry in PARENT_CONFIGS.
interface ParentConfig {
  /** Identifies a module as a parent by its display name. */
  matches: (name: string) => boolean;
  /** Header icon for the parent row (overrides the per-name auto-detect). */
  headerIcon: string;
  /** Ordered list of child rows. */
  subItems: SubModuleItem[];
}

type ProcessType = 'ausfuhr' | 'einfuhr' | 'eas';

const PARENT_CONFIGS: ParentConfig[] = [
  {
    matches: (name) => /atlas/i.test(name),
    headerIcon: moduleIcons.atlas,
    subItems: [
      { id: 'ausfuhr', label: 'Ausfuhr', iconKey: 'ausfuhr' },
      { id: 'einfuhr', label: 'Einfuhr', iconKey: 'einfuhr', disabled: true },
      { id: 'eas',     label: 'EAS',     iconKey: 'eas',     disabled: true },
    ],
  },
  // Add more parents here as the platform grows. Example:
  // {
  //   matches: (name) => /^fredi\b/i.test(name),
  //   headerIcon: moduleIcons.fredi,
  //   subItems: [...],
  // },
];

const findParentConfig = (name: string): ParentConfig | undefined =>
  PARENT_CONFIGS.find(c => c.matches(name));

// Returns true if the given Atlas sub-item id is currently disabled in
// PARENT_CONFIGS. Used to reject stale localStorage selections so users
// who previously chose Einfuhr/EAS land back on the enabled default.
const isAtlasSubItemDisabled = (id: string): boolean => {
  const atlas = PARENT_CONFIGS.find(c => c.matches('atlas'));
  return atlas?.subItems.find(s => s.id === id)?.disabled === true;
};

// Shape derived from the existing Module interface in DocumentProcessing.tsx.
// `auth_status` is NOT returned by GET /api/v1/modules directly — it is
// enriched per-module via authService.checkModuleAuthStatus(moduleId), mirroring
// the existing loadModules() pattern.
interface ApiModule {
  id: string;
  name: string;
  description?: string;
  is_active?: boolean;
  enabled?: boolean;
  auth_status?: 'authenticated' | 'expired' | 'unauthenticated';
}

const ICON_FOR_NAME = (raw: string): string => {
  const n = (raw || '').toLowerCase();
  if (n.includes('atlas')) return moduleIcons.atlas;
  if (n.includes('ausfuhr')) return moduleIcons.ausfuhr;
  if (n.includes('einfuhr')) return moduleIcons.einfuhr;
  if (/\beas\b/.test(n)) return moduleIcons.eas;
  if (n.includes('praferenz') || n.includes('präferenz')) return moduleIcons.praferenzKalkulation;
  if (n.includes('sanktion') || n.includes('sanction')) return moduleIcons.sanktionsList;
  if (n.includes('export') && n.includes('kontroll')) return moduleIcons.exportKontrolle;
  if (n.includes('fredi')) return moduleIcons.fredi;
  return uiIcons.dotFilled;
};

interface StatusPillProps {
  status?: ApiModule['auth_status'];
  onClick?: (e: React.MouseEvent) => void;
  labels: { authenticate: string; switchUser: string };
}

const StatusPill: React.FC<StatusPillProps> = ({ status, onClick, labels }) => {
  const isAuthenticated = status === 'authenticated' || status === 'expired';
  const label = isAuthenticated ? labels.switchUser : labels.authenticate;
  const bg = isAuthenticated ? brand.orange : brand.success;
  const color = isAuthenticated ? brand.black : brand.successText;
  return (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      icon={!isAuthenticated ? <LockOutlinedIcon /> : undefined}
      sx={{
        backgroundColor: bg,
        color,
        borderRadius: radii.pill,
        fontWeight: 700,
        fontSize: 11,
        height: 22,
        cursor: onClick ? 'pointer' : 'default',
        '& .MuiChip-icon': { color, fontSize: 14, ml: '6px', mr: '-3px' },
      }}
    />
  );
};

// Single row in the sub-items list. Pulled out so first-level rows and
// nested second-level (children) rows can share styling, with only the
// left indent differing.
interface SubRowProps {
  item: SubModuleItem;
  isActive: boolean;
  isAuthenticated: boolean;
  indentPx?: number;
  iconSize?: number;
  fontSize?: number;
  onClick: () => void;
}

const SubRow: React.FC<SubRowProps> = ({
  item, isActive, isAuthenticated, indentPx = 0, iconSize = 20, fontSize = 13, onClick,
}) => {
  const isEnabled = isAuthenticated && !item.disabled;
  return (
  <Box
    component="button"
    type="button"
    role="radio"
    aria-checked={isActive}
    aria-disabled={!isEnabled}
    disabled={!isEnabled}
    onClick={isEnabled ? onClick : undefined}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      width: `calc(100% - 16px - ${indentPx}px)`,
      ml: `${8 + indentPx}px`,
      mr: 1,
      mb: 0.5,
      px: 1.5,
      py: 0.75,
      border: isActive ? `1px solid ${brand.orange}` : '1px solid transparent',
      borderRadius: 1,
      backgroundColor: 'transparent',
      font: 'inherit',
      color: 'inherit',
      textAlign: 'left',
      cursor: isEnabled ? 'pointer' : 'not-allowed',
      opacity: isEnabled ? 1 : 0.5,
      '&:hover': isEnabled ? { backgroundColor: brand.dropZoneBg } : undefined,
      '&:focus-visible': {
        outline: `2px solid ${brand.orange}`,
        outlineOffset: 2,
      },
      '&:disabled': { cursor: 'not-allowed' },
    }}
  >
    <NavIcon src={moduleIcons[item.iconKey]} size={iconSize} />
    <Box sx={{ flex: 1, fontSize, fontWeight: 400, color: brand.textPrimary }}>
      {item.label}
    </Box>
    <Box
      sx={{
        width: 14, height: 14, borderRadius: '50%',
        border: `2px solid ${isActive ? RADIO_ACTIVE_GREEN : brand.borderSubtle}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {isActive && (
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: RADIO_ACTIVE_GREEN }} />
      )}
    </Box>
  </Box>
  );
};

interface ParentModuleCardProps {
  module: ApiModule;
  headerIcon: string;
  subItems: SubModuleItem[];
  selectedSubItemId: string | null;
  selectedSubItemChildId: string | null;
  isAuthenticated: boolean;
  onAuthClick: (kind: 'authenticate' | 'switch_user') => void;
  onSubItemClick: (subItemId: string) => void;
  onSubItemChildClick: (parentSubItemId: string, childId: string) => void;
  pillLabels: { authenticate: string; switchUser: string };
}

const ParentModuleCard: React.FC<ParentModuleCardProps> = ({
  module, headerIcon, subItems, selectedSubItemId, selectedSubItemChildId,
  isAuthenticated, onAuthClick, onSubItemClick, onSubItemChildClick, pillLabels,
}) => (
  <Box
    sx={{
      border: `1px solid ${brand.borderSubtle}`,
      borderRadius: '8px',
      mx: 1.5,
      my: 1,
      backgroundColor: '#fbfcf9',
      overflow: 'hidden',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1 }}>
      <NavIcon src={headerIcon} size={28} />
      <Box sx={{ flex: 1, fontSize: 14, fontWeight: 400, color: brand.textPrimary }}>
        {module.name}
      </Box>
      <StatusPill
        status={module.auth_status}
        labels={pillLabels}
        onClick={() => onAuthClick(
          (module.auth_status === 'authenticated' || module.auth_status === 'expired') ? 'switch_user' : 'authenticate'
        )}
      />
    </Box>

    <Box role="radiogroup" aria-label={`${module.name} sub-modules`}>
      {subItems.map(sub => {
        const isActive = selectedSubItemId === sub.id;
        return (
          <React.Fragment key={sub.id}>
            <SubRow
              item={sub}
              isActive={isActive}
              isAuthenticated={isAuthenticated}
              onClick={() => onSubItemClick(sub.id)}
            />
            {isActive && sub.children && sub.children.length > 0 && (
              <Box
                role="radiogroup"
                aria-label={`${sub.label} options`}
                sx={{ mb: 0.5 }}
              >
                {sub.children.map(child => (
                  <SubRow
                    key={child.id}
                    item={child}
                    isActive={selectedSubItemChildId === child.id}
                    isAuthenticated={isAuthenticated}
                    indentPx={24}
                    iconSize={16}
                    fontSize={12}
                    onClick={() => onSubItemChildClick(sub.id, child.id)}
                  />
                ))}
              </Box>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  </Box>
);

interface FlatModuleCardProps {
  module: ApiModule;
  isSelected: boolean;
  isAuthenticated: boolean;
  onCardClick: () => void;
  onAuthClick: (kind: 'authenticate' | 'switch_user') => void;
  pillLabels: { authenticate: string; switchUser: string };
}

const FlatModuleCard: React.FC<FlatModuleCardProps> = ({
  module, isSelected, isAuthenticated, onCardClick, onAuthClick, pillLabels,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      mx: 1.5,
      my: 1,
      border: isSelected ? `1px solid ${brand.orange}` : `1px solid ${brand.borderSubtle}`,
      borderRadius: '8px',
      backgroundColor: '#fbfcf9',
      overflow: 'hidden',
    }}
  >
    <Box
      component="button"
      type="button"
      onClick={onCardClick}
      aria-pressed={isSelected}
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1,
        backgroundColor: 'transparent',
        border: 0,
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        '&:hover': { backgroundColor: brand.dropZoneBg },
        '&:focus-visible': {
          outline: `2px solid ${brand.orange}`,
          outlineOffset: -2,
        },
      }}
    >
      <NavIcon src={ICON_FOR_NAME(module.name)} size={28} />
      <Box sx={{ flex: 1, fontSize: 14, fontWeight: 400, color: brand.textPrimary }}>
        {module.name}
      </Box>
    </Box>
    <Box sx={{ pr: 1.5, flexShrink: 0 }}>
      <StatusPill
        status={module.auth_status}
        labels={pillLabels}
        onClick={() => onAuthClick(
          (module.auth_status === 'authenticated' || module.auth_status === 'expired') ? 'switch_user' : 'authenticate'
        )}
      />
    </Box>
  </Box>
);

interface ProcessingStats {
  uploadedFiles: number;
  extracted: boolean;
  validationErrors: number;
}

const DrawerFooter: React.FC<{ stats: ProcessingStats }> = ({ stats }) => {
  if (stats.uploadedFiles === 0 && stats.validationErrors === 0) return null;
  return (
    <Box
      sx={{
        borderTop: `1px solid ${brand.borderSubtle}`,
        mt: 1,
        px: 2,
        py: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      {stats.uploadedFiles > 0 && (
        <Box sx={{ fontSize: 12, color: brand.textSecondary }}>
          {stats.uploadedFiles} file{stats.uploadedFiles !== 1 ? 's' : ''} uploaded
          {stats.extracted && ' · data extracted'}
        </Box>
      )}
      {stats.validationErrors > 0 && (
        <Box sx={{ fontSize: 12, color: brand.errorText, fontWeight: 600 }}>
          {stats.validationErrors} validation error{stats.validationErrors !== 1 ? 's' : ''}
        </Box>
      )}
    </Box>
  );
};

interface ModuleDrawerProps {
  selectedModuleId?: string;
}

const ModuleDrawer: React.FC<ModuleDrawerProps> = ({ selectedModuleId }) => {
  const { open, setOpen } = useModuleDrawerStore();
  const { tString } = useTranslation();
  const pillLabels = {
    authenticate: tString('moduleDrawer.authenticate') || 'Authenticate',
    switchUser: tString('moduleDrawer.switchUser') || 'Switch User',
  };
  const [modules, setModules] = useState<ApiModule[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedProcessType, setSelectedProcessType] = useState<ProcessType>(() => {
    try {
      const stored = localStorage.getItem('selectedProcessType');
      if ((stored === 'ausfuhr' || stored === 'einfuhr' || stored === 'eas') && !isAtlasSubItemDisabled(stored)) return stored;
    } catch { /* ignore */ }
    return 'ausfuhr';
  });

  // Second-level selection keyed by first-level sub-item id (e.g. 'ausfuhr').
  // Persisted as a JSON map in localStorage so each parent sub-item can have
  // its own remembered child. Defaults to the first child of any sub-item
  // that declares one — see lookupChildDefault below.
  // TODO(backend follow-up): replace localStorage persistence with a backend
  // call once sub-module selection becomes a server-side concern.
  const [selectedSubItemChildIds, setSelectedSubItemChildIds] = useState<Record<string, string>>(() => {
    try {
      const raw = localStorage.getItem('selectedSubItemChildIds');
      if (raw) return JSON.parse(raw) || {};
    } catch { /* ignore */ }
    return {};
  });

  const [stats, setStats] = useState<ProcessingStats>({ uploadedFiles: 0, extracted: false, validationErrors: 0 });

  useEffect(() => {
    const onProcessTypeChanged = (e: Event) => {
      const t = (e as CustomEvent).detail?.type;
      if ((t === 'ausfuhr' || t === 'einfuhr' || t === 'eas') && !isAtlasSubItemDisabled(t)) setSelectedProcessType(t);
    };
    const onStatsChanged = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) setStats(detail);
    };
    const onAuthChanged = (e: Event) => {
      const detail = (e as CustomEvent).detail as { moduleId?: string; authStatus?: 'authenticated' | 'expired' | 'unauthenticated' };
      if (!detail?.moduleId) return;
      setModules(prev => prev.map(m =>
        m.id === detail.moduleId
          ? { ...m, auth_status: detail.authStatus ?? m.auth_status }
          : m
      ));
    };
    window.addEventListener('process-type-changed', onProcessTypeChanged);
    window.addEventListener('processing-stats-changed', onStatsChanged);
    window.addEventListener('module-auth-changed', onAuthChanged);
    return () => {
      window.removeEventListener('process-type-changed', onProcessTypeChanged);
      window.removeEventListener('processing-stats-changed', onStatsChanged);
      window.removeEventListener('module-auth-changed', onAuthChanged);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const data = await authService.getModules();
        const list: ApiModule[] = Array.isArray(data) ? data : [];

        // Enrich each module with auth_status by calling the OAuth status endpoint
        // (mirrors the loadModules() pattern in DocumentProcessing.tsx).
        const enriched = await Promise.all(
          list.map(async (m: ApiModule) => {
            try {
              const statusResp = await authService.checkModuleAuthStatus(m.id);
              const auth_status: ApiModule['auth_status'] = statusResp?.authenticated
                ? 'authenticated'
                : 'unauthenticated';
              return { ...m, auth_status };
            } catch {
              return { ...m, auth_status: 'unauthenticated' as const };
            }
          })
        );

        if (!cancelled) setModules(enriched);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load modules for drawer:', err);
          setModules([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open]);

  // Resolves which sub-item (if any) is currently active for the given parent
  // module. Today only Atlas has sub-items, and its selection lives in
  // `selectedProcessType`. As more parents are added, route their selection
  // state here (e.g. via module name).
  const getSelectedSubItemId = (parentModuleName: string): string | null => {
    if (/atlas/i.test(parentModuleName)) return selectedProcessType;
    return null;
  };

  // Fires when the user clicks a sub-row inside a parent card. Atlas keeps its
  // dedicated event protocol (`process-type-selected`) consumed by
  // DocumentProcessing; other parents will need their own dispatch wiring
  // when they grow sub-items.
  const handleSubItemClick = (subItemId: string, parentModule: ApiModule) => {
    if (selectedModuleId !== parentModule.id) {
      window.dispatchEvent(new CustomEvent('module-selected', { detail: { id: parentModule.id } }));
    }
    if (/atlas/i.test(parentModule.name)) {
      window.dispatchEvent(new CustomEvent('process-type-selected', {
        detail: { type: subItemId as ProcessType, moduleId: parentModule.id },
      }));
    }
  };

  // Second-level click: persist the child selection per first-level sub-item,
  // then announce it on the window so downstream consumers (form view, ATLAS
  // submission payload, etc.) can branch on the variant choice. The drawer
  // remains the source of truth — there is no companion `*-changed` event,
  // because no other component owns this selection today.
  const handleSubItemChildClick = (parentSubItemId: string, childId: string, parentModule: ApiModule) => {
    setSelectedSubItemChildIds(prev => {
      const next = { ...prev, [parentSubItemId]: childId };
      try { localStorage.setItem('selectedSubItemChildIds', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    window.dispatchEvent(new CustomEvent('sub-process-variant-selected', {
      detail: {
        moduleId: parentModule.id,
        moduleName: parentModule.name,
        subItemId: parentSubItemId,
        variantId: childId,
      },
    }));
  };

  // Resolves the active child id under a given first-level sub-item, falling
  // back to the first declared child if no explicit selection is stored yet.
  const getSelectedSubItemChildId = (sub: SubModuleItem): string | null => {
    if (!sub.children || sub.children.length === 0) return null;
    const stored = selectedSubItemChildIds[sub.id];
    if (stored && sub.children.some(c => c.id === stored)) return stored;
    return sub.children[0].id;
  };

  // Resolves the active child id for the *currently selected* first-level
  // sub-item of a given parent module. The ParentModuleCard only renders the
  // children of the active sub-item, so this single lookup suffices.
  const getActiveChildIdForParent = (parentConfig: ParentConfig, parentModuleName: string): string | null => {
    const activeSubId = getSelectedSubItemId(parentModuleName);
    if (!activeSubId) return null;
    const activeSub = parentConfig.subItems.find(s => s.id === activeSubId);
    if (!activeSub) return null;
    return getSelectedSubItemChildId(activeSub);
  };

  const handleFlatCardClick = (m: ApiModule) => {
    window.dispatchEvent(new CustomEvent('module-selected', { detail: { id: m.id } }));
  };

  const handleAuthClick = (m: ApiModule, kind: 'authenticate' | 'switch_user') => {
    window.dispatchEvent(new CustomEvent('module-auth-requested', {
      detail: { moduleId: m.id, kind },
    }));
  };

  return (
    <>
      {/* Caret pointing from the header drawer-trigger down to the drawer top */}
      {open && (
        <Box
          aria-hidden
          sx={{
            position: 'fixed',
            top: 52,
            right: 16,
            width: 16,
            height: 8,
            backgroundColor: '#fff',
            clipPath: 'polygon(50% 0, 0% 100%, 100% 100%)',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            pointerEvents: 'none',
          }}
        />
      )}
    <Drawer
      anchor="right"
      open={open}
      onClose={() => setOpen(false)}
      variant="persistent"
      PaperProps={{
        id: 'module-drawer-panel',
        sx: {
          width: DRAWER_WIDTH,
          top: 60,
          height: 'calc(100vh - 60px)',
          borderLeft: `1px solid ${brand.borderSubtle}`,
          boxShadow: 'none',
          backgroundColor: '#fff',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 2,
          borderBottom: `1px solid ${brand.borderSubtle}`,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 16 }}>{tString('moduleDrawer.title') || 'Select Module'}</Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!loading && modules.length === 0 && (
          <Box sx={{ textAlign: 'center', color: brand.textMuted, fontSize: 13, py: 4 }}>
            {tString('moduleDrawer.empty') || 'No modules available'}
          </Box>
        )}
        {!loading && [
          // Flat modules render first; parent modules (Atlas et al.) sink to
          // the bottom so their nested sub-items don't visually interrupt the
          // top-level list. Filter is order-preserving, so within each group
          // the original API order is kept.
          ...modules.filter(m => !findParentConfig(m.name)),
          ...modules.filter(m => findParentConfig(m.name)),
        ].map(m => {
          const isAuthenticated = m.auth_status === 'authenticated';
          const parentConfig = findParentConfig(m.name);
          if (parentConfig) {
            return (
              <ParentModuleCard
                key={m.id}
                module={m}
                headerIcon={parentConfig.headerIcon}
                subItems={parentConfig.subItems}
                selectedSubItemId={getSelectedSubItemId(m.name)}
                selectedSubItemChildId={getActiveChildIdForParent(parentConfig, m.name)}
                isAuthenticated={isAuthenticated}
                onAuthClick={(kind) => handleAuthClick(m, kind)}
                onSubItemClick={(subItemId) => handleSubItemClick(subItemId, m)}
                onSubItemChildClick={(subItemId, childId) => handleSubItemChildClick(subItemId, childId, m)}
                pillLabels={pillLabels}
              />
            );
          }
          return (
            <FlatModuleCard
              key={m.id}
              module={m}
              isSelected={m.id === selectedModuleId}
              isAuthenticated={isAuthenticated}
              onCardClick={() => handleFlatCardClick(m)}
              onAuthClick={(kind) => handleAuthClick(m, kind)}
              pillLabels={pillLabels}
            />
          );
        })}

        <DrawerFooter stats={stats} />
      </Box>
    </Drawer>
    </>
  );
};

export default ModuleDrawer;
